import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { UI } from "./profileData";
import { withDefaults } from "@/lib/sections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", opportunity: "", message: "" };
const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#050f1f] px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors duration-200 focus:border-[#FF5500]/60 focus:outline-none";

export const RecruiterForm = ({ lang, content }) => {
  const t = UI[lang].form;
  const c = withDefaults("p_contact", content);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/messages`, {
        ...form,
        company: form.company || null,
        opportunity: form.opportunity || null,
      });
      setSent(true);
      setForm(EMPTY);
      toast.success(t.success);
    } catch {
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-12" id="contacto" data-testid="recruiter-section">
      <p className="font-mono text-xs uppercase tracking-widest text-slate-500">// contact</p>
      <h2 className="mt-2 text-base font-semibold text-white md:text-lg">{c[`title_${lang}`]}</h2>
      <p className="mt-2 max-w-xl text-sm text-slate-400">{c[`subtitle_${lang}`]}</p>
      {sent ? (
        <div className="mt-8 flex items-center gap-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-5 text-emerald-300" data-testid="recruiter-success">
          <CheckCircle2 className="h-5 w-5" /> {t.success}
          <button onClick={() => setSent(false)} className="ml-auto font-mono text-xs underline" data-testid="recruiter-send-another">{t.another}</button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 grid gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:grid-cols-2" data-testid="recruiter-form">
          <input required minLength={2} value={form.name} onChange={set("name")} placeholder={t.name} className={inputCls} data-testid="recruiter-name-input" />
          <input value={form.company} onChange={set("company")} placeholder={t.company} className={inputCls} data-testid="recruiter-company-input" />
          <input required type="email" value={form.email} onChange={set("email")} placeholder={t.email} className={inputCls} data-testid="recruiter-email-input" />
          <select value={form.opportunity} onChange={set("opportunity")} className={inputCls} data-testid="recruiter-opportunity-select">
            <option value="">{t.opportunity}</option>
            <option value="job">{t.opts.job}</option>
            <option value="freelance">{t.opts.freelance}</option>
            <option value="consulting">{t.opts.consulting}</option>
          </select>
          <textarea required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder={t.message} className={`${inputCls} sm:col-span-2`} data-testid="recruiter-message-input" />
          <button
            type="submit"
            disabled={loading}
            className="inline-flex w-fit items-center gap-2 rounded-full bg-[#FF5500] px-6 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
            data-testid="recruiter-submit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t.submit}
          </button>
        </form>
      )}
    </section>
  );
};

export default RecruiterForm;
